import { Button, Input, Tooltip } from 'antd';
import React, { useMemo } from 'react';
import {
  LeftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { useParams, useRouter } from '@/router/hooks';
import { useNovelCoreFormData } from '@/stores/novelCoreStore';
import { CoreEntityType } from '#/index';
import ThesisBriefPanel from '@/pages/ThesisBrief/ThesisBriefPanel';
import ThesisWorkspaceChrome from '@/components/ThesisWorkspaceChrome';

/**
 * 左侧：返回 + 可编辑标题/作者 + 开题卡。
 * 收起为窄轨；云同步/账号在正文顶栏（小屏展开时挂本条）。
 */
const Sidebar: React.FC<{ className?: string }> = ({ className }) => {
  const router = useRouter();
  const { novelId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formData, onFormChange, onFormSubmit } = useNovelCoreFormData(
    CoreEntityType.Novels,
    novelId!,
  );

  /** 默认收起开题卡，把宽度留给正文；仅 sidebar=open 时展开 */
  const sidebarHidden = useMemo(() => {
    return searchParams.get('sidebar') !== 'open';
  }, [searchParams]);

  const collapseBrief = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('sidebar');
    next.delete('sidebar[detail]');
    setSearchParams(next);
  };

  const expandBrief = () => {
    const next = new URLSearchParams(searchParams);
    next.set('sidebar', 'open');
    next.delete('sidebar[detail]');
    setSearchParams(next);
  };

  if (sidebarHidden) {
    return (
      <aside
        className={classNames(
          'novel-workspace-sidebar flex-none border-r flex flex-row lg:flex-col items-center gap-1 px-1.5 py-2 lg:w-14 min-h-14 z-10',
          className,
        )}
      >
        <Tooltip title="返回论文库" placement="right">
          <Button
            size="large"
            type="text"
            shape="circle"
            icon={<LeftOutlined />}
            onClick={() => router.push('/papers')}
          />
        </Tooltip>
        <Tooltip title="展开开题卡" placement="right">
          <Button
            size="large"
            type="text"
            shape="circle"
            icon={<MenuUnfoldOutlined />}
            onClick={expandBrief}
            aria-label="展开开题卡"
          />
        </Tooltip>
        <div
          className="hidden lg:flex flex-1 items-start justify-center pt-3 overflow-hidden"
          title={formData?.attributes.title || '开题卡'}
        >
          <span
            className="text-xs text-secondary select-none"
            style={{ writingMode: 'vertical-rl', letterSpacing: '0.12em' }}
          >
            {(formData?.attributes.title || '开题卡').slice(0, 12)}
          </span>
        </div>
        {/* 小屏：收起后仍要能进正文，顶栏在 main 里；这里补开题卡文字按钮 */}
        <button
          type="button"
          className="lg:hidden text-sm font-medium px-2 py-1 rounded hover:bg-fill-2"
          onClick={expandBrief}
        >
          开题卡
        </button>
      </aside>
    );
  }

  return (
    <aside
      className={classNames(
        'novel-workspace-sidebar lg:w-1/3 lg:max-w-[450px] grow lg:flex-none border-r flex flex-col overflow-hidden relative contain-strict min-h-14',
        className,
      )}
    >
      <header className="flex-none overflow-hidden z-10 lg:shadow px-2 py-2 flex items-start gap-1">
        <Button
          title="返回论文库"
          size="large"
          className="text-xl shrink-0"
          type="text"
          shape="circle"
          icon={<LeftOutlined />}
          onClick={() => router.push('/papers')}
        />
        <div className="flex-1 overflow-hidden min-w-0 flex flex-col gap-0.5 py-0.5">
          <Input
            variant="borderless"
            className="font-semibold text-base px-1 leading-tight"
            placeholder="论文标题"
            value={formData?.attributes.title || ''}
            onChange={(e) => onFormChange({ attributes: { title: e.target.value } })}
            onBlur={() => onFormSubmit()}
          />
          <Input
            variant="borderless"
            className="text-sm text-secondary px-1 leading-tight"
            placeholder="作者"
            value={formData?.attributes.author || ''}
            onChange={(e) => onFormChange({ attributes: { author: e.target.value } })}
            onBlur={() => onFormSubmit()}
          />
        </div>
        <div className="lg:hidden shrink-0 self-center">
          <ThesisWorkspaceChrome />
        </div>
        <Tooltip title="收起开题卡，回正文">
          <Button
            className="collapse-btn shrink-0 self-center"
            type="text"
            shape="circle"
            icon={<MenuFoldOutlined />}
            onClick={collapseBrief}
            aria-label="收起开题卡"
          />
        </Tooltip>
        <Button
          type="link"
          size="small"
          className="lg:hidden shrink-0 self-center px-1"
          onClick={collapseBrief}
        >
          回正文
        </Button>
      </header>

      <div className="flex sidebar-inner grow overflow-hidden">
        <div className="flex-1 overflow-hidden">
          <ThesisBriefPanel />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
